import { Injectable } from '@angular/core';
import { Drug } from 'src/API';
import { DryEyeMedicalHistoryFormGroup } from 'src/app/shared/consult-forms/dry-eye-forms/dry-eye-medical-history-form/dry-eye-medical-history-form.model';
import { splitMedicationFreeText } from './medication-free-text.util';

/**
 * Owns the single reconciliation of staged patient-entered medications into the
 * structured `medications` list. Pure (no Angular deps beyond @Injectable) and
 * therefore unit-testable in isolation.
 */
@Injectable({ providedIn: 'root' })
export class MedicationReconciler {
  /**
   * Folds staged patient-entered medications (free text or Drug objects from
   * any source) into `medications`, deduping by non-null id, or by name +
   * strength when there is no id. The id comparison is guarded against null
   * so distinct free-text meds (which all carry id: null) are not collapsed
   * into one, and the strength comparison ensures two different strengths of
   * the same medication (e.g. Synthroid 100mcg and Synthroid 112mcg) are kept
   * as separate entries instead of the second one being discarded.
   */
  reconcile(formGroup: DryEyeMedicalHistoryFormGroup): void {
    const existingMedications: Drug[] = formGroup.controls.medications.value || [];
    const rawValue = formGroup.controls.patientEnteredMedicationsFreeText.value;

    const processedEntries = splitMedicationFreeText(rawValue)
      .map(entry => (typeof entry === 'string' ? this.createDrugFromString(entry) : entry))
      .filter(entry => entry !== null && entry.name && entry.name.trim());

    const mergedMedications: Drug[] = [...existingMedications];
    processedEntries.forEach((medication: Drug) => {
      const alreadyPresent = mergedMedications.some(existing => this.isSameMedication(existing, medication));
      if (!alreadyPresent) {
        mergedMedications.push(medication);
      }
    });

    if (mergedMedications.length > existingMedications.length) {
      formGroup.controls.medications.patchValue(mergedMedications);
      formGroup.markAsDirty();
    }
  }

  private isSameMedication(existing: Drug, medication: Drug): boolean {
    if (medication.id !== null && medication.id !== undefined) {
      return existing.id === medication.id;
    }
    return existing.name === medication.name && this.strengthSignature(existing) === this.strengthSignature(medication);
  }

  /**
   * Builds an order-independent signature of a medication's active ingredient
   * strengths, so two Drug entries with the same name but different
   * strengths (or a different number of ingredients) are not treated as
   * duplicates.
   */
  private strengthSignature(medication: Drug): string {
    const ingredients = medication.active_ingredients || [];
    return ingredients
      .map(ingredient => {
        const name = ingredient && ingredient.ingredient ? ingredient.ingredient : '';
        const strength = ingredient && ingredient.strength !== null && ingredient.strength !== undefined ? ingredient.strength : '';
        const unit = ingredient && ingredient.strength_unit ? ingredient.strength_unit : '';
        return name + '|' + strength + '|' + unit;
      })
      .sort()
      .join(',');
  }

  createDrugFromString(name: string): Drug {
    return {
      __typename: 'Drug',
      id: null,
      name: name,
      active_ingredients: [
        {
          __typename: 'DrugActiveIngredients',
          ingredient: 'Ingredient not specified',
          strength: null,
          strength_unit: ''
        }
      ],
      routes: [
        {
          __typename: 'DrugRoute',
          route_of_administration: 'Route not specified'
        }
      ],
      dosage_form: 'Dosage form not specified'
    };
  }
}
